import {
  type ErrorType,
  ImportAttemptEvent,
  ImportCheckingEvent,
  ImportEndedEvent,
  ImportFailEvent,
  ImportFileStartedEvent,
  ImportLimitedImportsEvent,
  ImportS3AttemptEvent,
  ImportS3FailEvent,
  ImportS3SuccessEvent,
  ImportStartedEvent,
  ImportSuccessEvent,
  LimitStatus,
  PremiumFeature,
  PurchaseOpenAccountPlanUpgradeEvent,
  trackEvent,
  UpgradePlanType,
  UpgradeTrigger,
} from '@otter-ai/telemetry';
import type { ImportQuotas, UploadItem } from '../types/types';
import {
  hasRemainingImportQuota,
  isLifetimeImportLimitApplicable,
} from '../utils/utils';

export function importAttemptedEvent() {
  trackEvent(ImportAttemptEvent());
}

export function importStartedEvent(items: UploadItem[]) {
  trackEvent(
    ImportStartedEvent({
      Data: items.map((item) => item.file.name).join(';'),
    }),
  );
}

export function importFileStartedEvent(item: UploadItem) {
  trackEvent(
    ImportFileStartedEvent({
      Data: item.file.name,
    }),
  );
}

export function importFailedEvent(item: UploadItem, errorType: ErrorType) {
  trackEvent(
    ImportFailEvent({
      ErrorType: errorType,
      Data: item.file.name,
    }),
  );
}

export function importCheckingEvent(item: UploadItem, duration: number) {
  trackEvent(
    ImportCheckingEvent({
      Data: item.file.name,
      Duration: duration,
    }),
  );
}

export function importS3AttemptEvent(item: UploadItem) {
  trackEvent(
    ImportS3AttemptEvent({
      Data: item.file.name,
    }),
  );
}

export function importS3FailedEvent(item: UploadItem) {
  trackEvent(
    ImportS3FailEvent({
      Data: item.file.name,
    }),
  );
}

export function importS3SuccessEvent(
  item: UploadItem,
  key: string | undefined,
) {
  trackEvent(
    ImportS3SuccessEvent({
      Data: item.file.name,
      Key: key ? key : undefined,
    }),
  );
}

export function importSuccessEvent(item: UploadItem) {
  trackEvent(
    ImportSuccessEvent({
      Data: item.file.name,
    }),
  );
}

export function importEndedEvent(successCount: number, failureCount: number) {
  trackEvent(
    ImportEndedEvent({
      UploadSuccessCount: successCount,
      UploadFailCount: failureCount,
    }),
  );
}

export function limitedImportsEvent(quota: ImportQuotas) {
  // Return whichever is not null, or undefined if both are null
  const importsCount =
    quota.importQuota ?? quota.monthlyImportQuota ?? undefined;

  if (importsCount === undefined) {
    return;
  }

  trackEvent(
    ImportLimitedImportsEvent({
      ImportsCount: importsCount,
    }),
  );
}

export function purchaseOpenAccountPlanUpgradeEvent(quota: ImportQuotas) {
  const premiumFeature = isLifetimeImportLimitApplicable(quota)
    ? PremiumFeature.lifetimeImport
    : PremiumFeature.monthlyImport;

  const limitStatus = hasRemainingImportQuota(quota)
    ? LimitStatus.limitApproaching
    : LimitStatus.limitReached;

  trackEvent(
    PurchaseOpenAccountPlanUpgradeEvent({
      UpgradePlanType: UpgradePlanType.team,
      PremiumFeature: premiumFeature,
      LimitStatus: limitStatus,
      UpgradeTrigger: UpgradeTrigger.featureInteraction,
    }),
  );
}
